import { SYNC_PUSH_MAX, type SyncOp, type SyncOpResult } from '@wr/shared';
import { ApiError } from '../api.js';
import { RECEIPT_PREFIX, type LocalRecord, type LocalStore } from './store.js';

/**
 * Sends the outbox to the server, then pulls what changed (brief section 9).
 * Each change has its own opId, so sending it again after a lost answer is
 * harmless. Without network, changes wait for the next run; when the
 * server fails, they wait longer each time.
 */
/** The latest reset of the portal's data (round 2, staging). */
export interface DataReset {
  id: string;
  at: string;
}

export interface SyncTransport {
  /** resetId: the latest data reset this device knows of; the server refuses changes made before another. */
  push(
    ops: SyncOp[],
    resetId: string | null,
  ): Promise<{ results: SyncOpResult[]; reset?: DataReset | null }>;
  pull(cursor: number, eventId: string | null): Promise<PullPage>;
  /** Sends the photo of a scanned card, after its contact. */
  upload?(contactId: string, photo: Blob): Promise<unknown>;
}

export interface PullPage {
  cursor: number;
  more: boolean;
  events: LocalRecord[];
  contacts: LocalRecord[];
  leads: LocalRecord[];
  leadContacts: (Record<string, unknown> & {
    lead_id: string;
    contact_id: string;
    removed?: boolean;
  })[];
  eventNotes: LocalRecord[];
  /** The user's own receipts (step 8); absent from an older server. */
  expenses?: LocalRecord[];
  /** Meetings (Nov 3, 2026): the current event's and the user's; absent from an older server. */
  meetings?: LocalRecord[];
  /** Round 2: the latest reset of the portal's data; absent from an older server. */
  reset?: DataReset | null;
}

/** A reset this device has not seen: it empties what it kept from before (round 2). */
async function seeReset(store: LocalStore, reset: DataReset | null | undefined): Promise<boolean> {
  if (!reset || reset.id === (await store.getMeta<string>('resetId'))) return false;
  await store.emptyForReset(Date.parse(reset.at));
  await store.setMeta('resetId', reset.id);
  return true;
}

export type SyncOutcome = 'done' | 'offline' | 'needs_sign_in' | 'server_error';

/** Waiting time after a server failure: 5 s, 10 s, 20 s... up to 5 minutes. */
export function backoffMs(attempts: number): number {
  return Math.min(5_000 * 2 ** Math.max(0, attempts - 1), 5 * 60_000);
}

export async function runSync(
  store: LocalStore,
  transport: SyncTransport,
  now: () => number = Date.now,
  afterReset = false,
): Promise<SyncOutcome> {
  // 1. Push what is due, oldest first, in batches.
  const due = (await store.outbox()).filter(
    (i) => i.status === 'pending' && i.nextAttemptAt <= now(),
  );
  for (let start = 0; start < due.length; start += SYNC_PUSH_MAX) {
    const batch = due.slice(start, start + SYNC_PUSH_MAX);
    let results: SyncOpResult[];
    try {
      const answer = await transport.push(
        batch.map((i) => i.op),
        await store.getMeta<string>('resetId'),
      );
      // The data were reset: empty what came before, then send what was made after.
      if (answer.reset && !afterReset && (await seeReset(store, answer.reset))) {
        return runSync(store, transport, now, true);
      }
      results = answer.results;
    } catch (error) {
      const outcome = failure(error);
      if (outcome === 'needs_sign_in') return outcome;
      // No network: try again at the next run, it costs nothing. The server
      // failing: wait longer each time, so a struggling server can recover.
      for (const item of batch) {
        const attempts = item.attempts + 1;
        const wait = outcome === 'offline' ? 0 : backoffMs(attempts);
        await store.retryLater(item.opId, attempts, now() + wait);
      }
      return outcome;
    }
    for (const result of results) {
      if (result.status === 'applied') await store.remove(result.opId);
      else await store.reject(result.opId, result.error);
    }
  }

  // 1b. Send the photos of scanned cards whose contact the server has.
  if (transport.upload) {
    // A photo goes after its record (a contact, or a receipt as "expense:<id>").
    const unsent = new Set(
      (await store.outbox()).flatMap((i) =>
        i.op.action === 'upsert' && (i.op.entity === 'contact' || i.op.entity === 'expense')
          ? [i.op.entity === 'expense' ? `${RECEIPT_PREFIX}${i.op.id}` : i.op.id]
          : [],
      ),
    );
    const photos = (await store.photos()).filter(
      (p) => p.status === 'pending' && p.nextAttemptAt <= now() && !unsent.has(p.contactId),
    );
    for (const item of photos) {
      const photo = await store.photo(item.contactId);
      if (!photo) continue;
      try {
        await transport.upload(item.contactId, photo);
        await store.removePhoto(item.contactId);
      } catch (error) {
        const outcome = failure(error);
        if (outcome === 'needs_sign_in') return outcome;
        if (outcome === 'offline') {
          await store.retryPhotoLater(item.contactId, item.attempts + 1, now());
          return outcome;
        }
        const status = error instanceof ApiError ? error.status : 0;
        if (PHOTO_REFUSED.has(status)) {
          await store.rejectPhoto(item.contactId, photoError(error as ApiError));
        } else {
          // The server failing, or the contact not there yet: later.
          const attempts = item.attempts + 1;
          await store.retryPhotoLater(item.contactId, attempts, now() + backoffMs(attempts));
        }
      }
    }
  }

  // 2. Pull what changed since the last time (from 0 when the current event changes).
  try {
    const eventId = await store.getMeta<string>('eventId');
    const pulledFor = await store.getMeta<string | null>('pulledEventId');
    let cursor =
      pulledFor === (eventId ?? null) ? ((await store.getMeta<number>('cursor')) ?? 0) : 0;
    for (;;) {
      const page = await transport.pull(cursor, eventId ?? null);
      if (!afterReset && (await seeReset(store, page.reset))) {
        return runSync(store, transport, now, true);
      }
      await merge(store, page);
      cursor = page.cursor;
      await store.setMeta('cursor', cursor);
      await store.setMeta('pulledEventId', eventId ?? null);
      if (!page.more) break;
    }
  } catch (error) {
    return failure(error);
  }
  return 'done';
}

async function merge(store: LocalStore, page: PullPage): Promise<void> {
  const lists = [
    ['event', page.events],
    ['contact', page.contacts],
    ['lead', page.leads],
    ['event_note', page.eventNotes],
    ['expense', page.expenses ?? []],
    ['meeting', page.meetings ?? []],
  ] as const;
  for (const [entity, records] of lists) {
    for (const record of records) {
      if (record.deleted) await store.deleteRecord(entity, record.id);
      else await store.putRecord(entity, record);
    }
  }
  for (const link of page.leadContacts) {
    const id = `${link.lead_id}/${link.contact_id}`;
    if (link.removed) await store.deleteRecord('lead_contact', id);
    else await store.putRecord('lead_contact', { ...link, id });
  }
}

/** What sending again would not change: kept on the device, shown in Changes not sent. */
const PHOTO_REFUSED = new Set([403, 410, 413, 415]);

function photoError(error: ApiError): string {
  if (error.status === 413) return 'too_large';
  return error.code;
}

function failure(error: unknown): SyncOutcome {
  if (error instanceof ApiError && error.status === 401) return 'needs_sign_in';
  if (error instanceof ApiError && error.code === 'offline') return 'offline';
  return 'server_error';
}
